export const isBuilt = import.meta.env.PROD;
export const isDevelopment = import.meta.env.DEV;
export const isOnLocalhost = ["localhost", "127.0.0.1"].includes(
  window.location.hostname,
);

/**
 * Every Vercel-hosted origin is a preview deploy: production and staging are
 * served from `workspace.weld.app` and `workspace.staging.weld.app`.
 *
 * Matching the hostname suffix rather than the shape of a deployment URL is
 * load-bearing. The previous `web-\w+-weldhq.vercel.app` regex missed branch
 * aliases such as `web-git-my-branch-weldhq.vercel.app`, because `\w` does not
 * match `-`. Those previews fell through to `isOnProductionDomain`, which
 * selects the *production* Firebase project (features/firebase/config.ts) and
 * makes HubSpot submissions count as real.
 */
export const isOnPreviewDomain =
  window.location.hostname.endsWith(".vercel.app");

/**
 * Excludes preview hosts so a branch literally named "staging" is still
 * classified as the preview it is.
 */
export const isOnStagingDomain =
  !isOnPreviewDomain && window.location.hostname.includes("staging");
export const isOnProductionDomain =
  isBuilt && !isOnStagingDomain && !isOnPreviewDomain && !isOnLocalhost;

export function getEnvironment() {
  if (isOnProductionDomain) {
    return "production";
  } else if (isOnStagingDomain) {
    return "staging";
  } else if (isOnPreviewDomain) {
    return "preview";
  } else if (isOnLocalhost) {
    return "localhost";
  } else {
    return "unknown";
  }
}

/**
 * The deployed environment, or `undefined` when this is not a real deploy —
 * a development build, localhost, or an origin we do not recognise.
 *
 * Prefer this over {@link getEnvironment} for anything that should only happen
 * on a deploy, such as monitoring and analytics. The build and host checks are
 * part of the contract, so callers do not need to repeat them.
 *
 * Derived from the hostname rather than a build-time variable: the same artifact
 * is deployed to every environment, so where it is running is the reliable
 * signal. Monitoring used to read `VITE_VERCEL_ENV`, which was never defined in
 * Vercel, so preview deploys reported themselves as production — and because
 * `sessionReplaySampleRate` is keyed on that, session replay recorded there too.
 */
export function getDeployedEnvironment() {
  if (!isBuilt) {
    return undefined;
  }
  const environment = getEnvironment();
  return environment === "localhost" || environment === "unknown"
    ? undefined
    : environment;
}

export type DeployedEnvironmentName = ReturnType<typeof getDeployedEnvironment>;
